import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Mail, Plus, Edit, Eye, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import EmailTemplateSeedData from '@/components/admin/EmailTemplateSeedData';
import EmailTemplateBuilder from '@/components/email/EmailTemplateBuilder';

export default function EmailTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    template_key: '',
    name: '',
    subject: '',
    body_html: '',
    variables: [],
    category: 'system'
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await api.entities.EmailTemplate.list('-created_at');
      setTemplates(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    setFormData({
      template_key: '',
      name: '',
      subject: '',
      body_html: '',
      variables: [],
      category: 'system'
    });
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData(template);
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingTemplate) {
        await api.entities.EmailTemplate.update(editingTemplate.id, formData);
        toast.success('Template updated');
      } else {
        await api.entities.EmailTemplate.create(formData);
        toast.success('Template created');
      }
      setShowModal(false);
      loadTemplates();
    } catch (err) {
      toast.error('Failed to save template');
    }
  };

  const handlePreview = (template) => {
    setFormData(template);
    setShowPreview(true);
  };

  const defaultTemplate = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
    .container { max-width: 600px; margin: 0 auto; padding: 20px; }
    .header { background: linear-gradient(135deg, #10b981 0%, #14b8a6 100%); color: white; padding: 30px; text-align: center; }
    .content { padding: 30px; background: #f9fafb; }
    .button { display: inline-block; padding: 12px 24px; background: #10b981; color: white; text-decoration: none; border-radius: 6px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>{{companyName}}</h1>
    </div>
    <div class="content">
      <h2>Hello {{employeeName}},</h2>
      <p>Your email content goes here...</p>
      <a href="{{dashboardUrl}}" class="button">View Dashboard</a>
    </div>
  </div>
</body>
</html>`;

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Email Templates</h1>
          <p className="text-slate-500 mt-1">Manage and customize email notifications</p>
        </div>
        <Button onClick={handleAdd} className="self-start sm:self-auto bg-emerald-500 hover:bg-emerald-600">
          <Plus className="w-4 h-4 mr-2" />
          Add Template
        </Button>
      </div>

      {templates.length === 0 && !loading && (
        <EmailTemplateSeedData onComplete={loadTemplates} />
      )}

      <Tabs defaultValue="all" className="w-full">
        <div className="w-full overflow-x-auto">
          <TabsList className="flex w-max min-w-full justify-start h-auto py-1">
            <TabsTrigger value="all" className="flex-shrink-0 whitespace-nowrap">All Templates</TabsTrigger>
            <TabsTrigger value="enrollment" className="flex-shrink-0 whitespace-nowrap">Enrollment</TabsTrigger>
            <TabsTrigger value="claim" className="flex-shrink-0 whitespace-nowrap">Claims</TabsTrigger>
            <TabsTrigger value="qle" className="flex-shrink-0 whitespace-nowrap">Life Events</TabsTrigger>
            <TabsTrigger value="reminder" className="flex-shrink-0 whitespace-nowrap">Reminders</TabsTrigger>
          </TabsList>
        </div>

        {['all', 'enrollment', 'claim', 'qle', 'reminder'].map(tab => (
          <TabsContent key={tab} value={tab} className="space-y-4">
            {templates
              .filter(t => tab === 'all' || t.category === tab)
              .map(template => (
                <Card key={template.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <CardTitle className="flex items-center gap-2">
                          <Mail className="w-5 h-5 text-blue-500" />
                          {template.name}
                          <Badge variant="outline">{template.category}</Badge>
                        </CardTitle>
                        <p className="text-sm text-slate-600 font-mono bg-slate-50 px-2 py-1 rounded">
                          {template.template_key}
                        </p>
                        <p className="text-sm text-slate-500">Subject: {template.subject}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handlePreview(template)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {template.variables?.length > 0 && (
                      <div className="text-sm">
                        <p className="font-medium text-slate-700 mb-2">Available Variables:</p>
                        <div className="flex flex-wrap gap-2">
                          {template.variables.map(v => (
                            <code key={v} className="bg-slate-100 px-2 py-1 rounded text-xs">{`{{${v}}}`}</code>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
          </TabsContent>
        ))}
      </Tabs>

      {/* Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh] overflow-y-auto bg-background text-foreground">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Email Template' : 'Add Email Template'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Template Key</Label>
                <Input
                  value={formData.template_key}
                  onChange={(e) => setFormData({...formData, template_key: e.target.value})}
                  placeholder="e.g., enrollment_confirmation"
                  disabled={!!editingTemplate}
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <select 
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="enrollment">Enrollment</option>
                  <option value="claim">Claims</option>
                  <option value="qle">Life Events</option>
                  <option value="reminder">Reminders</option>
                  <option value="system">System</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Enrollment Confirmation Email"
              />
            </div>

            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder="e.g., Your Benefits Enrollment is Confirmed"
              />
            </div>

            <div className="space-y-2">
              <Label>Email Content</Label>
              <EmailTemplateBuilder
                value={formData.body_html}
                onChange={(html) => setFormData({...formData, body_html: html})}
                availableVariables={formData.variables || []}
              />
            </div>

            <div className="space-y-2">
              <Label>Available Variables (comma-separated)</Label>
              <Input
                value={formData.variables?.join(', ')}
                onChange={(e) => setFormData({...formData, variables: e.target.value.split(',').map(v => v.trim())})}
                placeholder="employeeName, productName, effectiveDate"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} className="bg-emerald-500 hover:bg-emerald-600">
                <Save className="w-4 h-4 mr-2" />
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium text-slate-700">Subject:</p>
              <p className="text-lg">{formData.subject}</p>
            </div>
            <div className="border rounded-lg p-4 bg-white h-[600px] overflow-auto">
              <iframe 
                srcDoc={formData.body_html} 
                className="w-full h-full border-0"
                title="Email Preview"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}